import { supabase } from '../lib/supabase';

export interface ContentSession {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  color: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface ContentItem {
  id: string;
  session_id: string;
  title: string;
  description: string;
  image_url: string;
  video_count: string;
  progress: number;
  is_completed: boolean;
  tags: string[];
  order_index: number;
  created_at: string;
  updated_at: string;
}

// Sessões
export const loadSessions = async (): Promise<ContentSession[]> => {
  try {
    const { data, error } = await supabase
      .from('content_sessions')
      .select('*')
      .order('order_index', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Erro ao carregar sessões:', error);
    return [];
  }
};

export const createSession = async (session: Omit<ContentSession, 'id' | 'created_at' | 'updated_at'>): Promise<ContentSession | null> => {
  try {
    const { data, error } = await supabase
      .from('content_sessions')
      .insert(session)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erro ao criar sessão:', error);
    return null;
  }
};

export const updateSession = async (id: string, updates: Partial<ContentSession>): Promise<ContentSession | null> => {
  try {
    const { data, error } = await supabase
      .from('content_sessions')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erro ao atualizar sessão:', error);
    return null;
  }
};

export const deleteSession = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('content_sessions')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erro ao excluir sessão:', error);
    return false;
  }
};

// Conteúdos
export const loadContentItems = async (sessionId?: string): Promise<ContentItem[]> => {
  try {
    let query = supabase
      .from('content_items')
      .select('*')
      .order('order_index', { ascending: true });

    if (sessionId) {
      query = query.eq('session_id', sessionId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Erro ao carregar conteúdos:', error);
    return [];
  }
};

export const createContentItem = async (content: Omit<ContentItem, 'id' | 'created_at' | 'updated_at'>): Promise<ContentItem | null> => {
  try {
    const { data, error } = await supabase
      .from('content_items')
      .insert(content)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erro ao criar conteúdo:', error);
    return null;
  }
};

export const updateContentItem = async (id: string, updates: Partial<ContentItem>): Promise<ContentItem | null> => {
  try {
    const { data, error } = await supabase
      .from('content_items')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erro ao atualizar conteúdo:', error);
    return null;
  }
};

export const deleteContentItem = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('content_items')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erro ao excluir conteúdo:', error);
    return false;
  }
};

export interface HeroConfig {
  headline: string;
  video_url: string;
  tools: {
    name: string;
    image_url: string;
    href: string;
  }[];
  mastershop_button?: {
    name: string;
    href: string;
  };
}

export const loadHeroConfig = async (): Promise<HeroConfig> => {
  try {
    const { data, error } = await supabase
      .from('media_config')
      .select('*')
      .in('key', ['hero_headline', 'hero_video_url', 'hero_tools', 'hero_mastershop_button']);

    if (error) throw error;

    const config: Partial<HeroConfig> = {};
    data?.forEach((item) => {
      if (item.key === 'hero_headline') {
        config.headline = item.value;
      } else if (item.key === 'hero_video_url') {
        config.video_url = item.value;
      } else if (item.key === 'hero_tools') {
        try {
          config.tools = JSON.parse(item.value);
        } catch (e) {
          console.error('Error parsing hero tools config', e);
        }
      } else if (item.key === 'hero_mastershop_button') {
        try {
          config.mastershop_button = JSON.parse(item.value);
        } catch (e) {
          console.error('Error parsing mastershop button config', e);
        }
      }
    });

    // Fallbacks padrão caso não haja no banco
    return {
      headline: config.headline || 'HEADLINE ECOSSISTEMA MASTERSHOP',
      video_url: config.video_url || 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      tools: config.tools || [
        { name: 'YOUTUBE', image_url: 'https://upload.wikimedia.org/wikipedia/commons/e/ef/Youtube_logo_icon_with_only_play_button_%282017%29.svg', href: 'https://youtube.com' },
        { name: 'COMUNIDADE ZAP', image_url: 'https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg', href: 'https://whatsapp.com' },
        { name: 'INSTA', image_url: 'https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png', href: 'https://instagram.com' },
        { name: 'PRECIFY', image_url: 'https://raw.githubusercontent.com/lojagenericaz/imagens_masterclub/main/images.png', href: '#' },
        { name: 'MASTERCOPY', image_url: 'https://raw.githubusercontent.com/lojagenericaz/imagens_masterclub/main/445849504_466082632762465_2258297688877912564_n.jpg', href: '#' }
      ],
      mastershop_button: config.mastershop_button || {
        name: 'MASTERSHOP',
        href: '#'
      }
    };
  } catch (error) {
    console.error('Erro ao carregar hero config:', error);
    return {
      headline: 'HEADLINE ECOSSISTEMA MASTERSHOP',
      video_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      tools: [
        { name: 'YOUTUBE', image_url: 'https://upload.wikimedia.org/wikipedia/commons/e/ef/Youtube_logo_icon_with_only_play_button_%282017%29.svg', href: 'https://youtube.com' },
        { name: 'COMUNIDADE ZAP', image_url: 'https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg', href: 'https://whatsapp.com' },
        { name: 'INSTA', image_url: 'https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png', href: 'https://instagram.com' },
        { name: 'PRECIFY', image_url: 'https://raw.githubusercontent.com/lojagenericaz/imagens_masterclub/main/images.png', href: '#' },
        { name: 'MASTERCOPY', image_url: 'https://raw.githubusercontent.com/lojagenericaz/imagens_masterclub/main/445849504_466082632762465_2258297688877912564_n.jpg', href: '#' }
      ],
      mastershop_button: {
        name: 'MASTERSHOP',
        href: '#'
      }
    };
  }
};

export const saveHeroConfig = async (config: HeroConfig): Promise<boolean> => {
  try {
    const upserts = [
      { key: 'hero_headline', value: config.headline, updated_at: new Date().toISOString() },
      { key: 'hero_video_url', value: config.video_url, updated_at: new Date().toISOString() },
      { key: 'hero_tools', value: JSON.stringify(config.tools), updated_at: new Date().toISOString() },
      { key: 'hero_mastershop_button', value: JSON.stringify(config.mastershop_button || { name: 'MASTERSHOP', href: '#' }), updated_at: new Date().toISOString() }
    ];

    const { error } = await supabase
      .from('media_config')
      .upsert(upserts);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erro ao salvar hero config:', error);
    return false;
  }
};
 